import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fileUrl, STATUS_OPTIONS } from "../../lib/api";
import { formatApiError } from "../../lib/auth";
import { StatusBadge } from "../../components/StatusBadge";
import { AudioPlayer } from "../../components/AudioPlayer";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Search, Download, Trash2, Edit3, ExternalLink, AlertCircle, CheckCheck, Eye, FileDown } from "lucide-react";
import { Checkbox } from "../../components/ui/checkbox";

export default function AdminSubmissions() {
  const [params] = useSearchParams();
  const initialStatus = params.get("status") || "";
  const initialPending = params.get("pending") === "1";

  const [subs, setSubs] = useState([]);
  const [filters, setFilters] = useState({ status: initialStatus, q: "", date_from: "", date_to: "", pending_review: initialPending });
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [downloading, setDownloading] = useState(false);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => prev.size === subs.length ? new Set() : new Set(subs.map((s) => s.id)));
  };

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (selected.size > 0) {
        params.set("ids", Array.from(selected).join(","));
      } else {
        Object.entries(filters).forEach(([k, v]) => {
          if (v === true) params.set(k, "true");
          else if (v) params.set(k, v);
        });
      }
      const token = localStorage.getItem("sd_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/submissions/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      a.download = m ? m[1] : `sonotekdigi_submissions_${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${selected.size > 0 ? selected.size : subs.length} submissions`);
    } catch (e) {
      toast.error("Excel download failed");
    } finally {
      setDownloading(false);
    }
  };

  const load = () => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v === true) p[k] = "true";
      else if (v) p[k] = v;
    });
    api.get("/admin/submissions", { params: p }).then((r) => setSubs(r.data));
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [filters]);

  const del = async (id) => {
    try { await api.delete(`/admin/submissions/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const save = async () => {
    const { id, ...rest } = editing;
    delete rest.user; delete rest.change_history; delete rest.pending_review;
    delete rest.artwork_url; delete rest.audio_url; delete rest.audio_format;
    delete rest.created_at; delete rest.last_edited_at; delete rest.user_id;
    delete rest.artwork_drive_url; delete rest.audio_drive_url;
    try {
      await api.patch(`/admin/submissions/${id}`, rest);
      toast.success("Updated"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
  };

  const markReviewed = async (id) => {
    try { await api.post(`/admin/submissions/${id}/mark-reviewed`); toast.success("Marked reviewed"); load(); setViewing(null); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Administrator</div>
      <h1 className="mt-2 text-4xl font-bold tracking-tight">Submissions</h1>

      <div className="mt-8 grid md:grid-cols-5 gap-3">
        <div className="relative md:col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input placeholder="Search song / artist / ISRC / UPC / ID / user..." value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            data-testid="admin-subs-search" className="pl-9 bg-zinc-900 border-zinc-800" />
        </div>
        <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="admin-subs-status-filter" className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
          data-testid="admin-subs-date-from" className="bg-zinc-900 border-zinc-800" />
        <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
          data-testid="admin-subs-date-to" className="bg-zinc-900 border-zinc-800" />
      </div>

      <div className="mt-2 sm:mt-3 flex flex-wrap items-center justify-between gap-3 text-sm">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={filters.pending_review}
            onChange={(e) => setFilters({ ...filters, pending_review: e.target.checked })}
            className="accent-cyan-400" data-testid="filter-pending-review" />
          <span className="text-zinc-400">Only songs with pending edit review</span>
        </label>
        <div className="flex items-center gap-3">
          {selected.size > 0 && (
            <span className="text-xs text-cyan-400" data-testid="selected-count">{selected.size} selected</span>
          )}
          <Button onClick={downloadExcel} disabled={downloading || subs.length === 0}
            data-testid="download-excel-btn"
            className="rounded-full bg-gradient-to-r from-emerald-400 to-cyan-400 text-zinc-950 font-semibold hover:brightness-110">
            <FileDown className="w-4 h-4 mr-2" />
            {downloading ? "Preparing..." : (selected.size > 0 ? `Download ${selected.size} as Excel` : "Download Excel")}
          </Button>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-x-auto">
        <table className="w-full min-w-[1000px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="p-4 w-10">
                <input type="checkbox" checked={subs.length > 0 && selected.size === subs.length}
                  onChange={toggleAll} data-testid="select-all-subs" className="accent-cyan-400 cursor-pointer" />
              </th>
              <th className="p-4">Song</th>
              <th className="p-4">Primary Artist</th>
              <th className="p-4">User</th>
              <th className="p-4">ISRC / UPC</th>
              <th className="p-4">Date</th>
              <th className="p-4">Status</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {subs.map((s) => (
              <tr key={s.id} data-testid={`sub-row-${s.id}`}
                className={`border-b border-zinc-900 hover:bg-zinc-900/40 ${s.pending_review ? "bg-amber-500/5" : ""} ${selected.has(s.id) ? "bg-cyan-500/5" : ""}`}>
                <td className="p-4">
                  <input type="checkbox" checked={selected.has(s.id)}
                    onChange={() => toggleSelect(s.id)}
                    data-testid={`select-${s.id}`}
                    className="accent-cyan-400 cursor-pointer" />
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <img src={fileUrl(s.artwork_url)} className="w-10 h-10 rounded-md object-cover border border-zinc-800" alt="" />
                    <div>
                      <div className="font-medium flex items-center gap-2">
                        {s.song_title}
                        {s.pending_review && (
                          <span data-testid={`pending-badge-${s.id}`} className="px-1.5 py-0.5 rounded-md bg-amber-500/20 border border-amber-500/40 text-amber-300 text-[10px] uppercase tracking-wider font-semibold inline-flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" /> Changes Pending
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500 font-mono">{s.id.slice(0, 8)}...</div>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-zinc-300">{s.primary_artist_name || s.artist_name}</td>
                <td className="p-4 text-zinc-300 text-sm">{s.user?.full_name}<br /><span className="text-zinc-500 text-xs">{s.user?.email}</span></td>
                <td className="p-4 text-xs font-mono text-emerald-400">{s.isrc || "—"}<br />{s.upc || "—"}</td>
                <td className="p-4 text-zinc-500 text-sm">{new Date(s.created_at).toLocaleDateString()}</td>
                <td className="p-4"><StatusBadge status={s.status} /></td>
                <td className="p-4">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={() => setViewing(s)} className="h-8 w-8 text-zinc-400 hover:text-cyan-400" data-testid={`view-${s.id}`}><Eye className="w-4 h-4" /></Button>
                    <a href={fileUrl(s.artwork_url)} target="_blank" rel="noreferrer" title="Artwork">
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-zinc-400 hover:text-cyan-400"><ExternalLink className="w-4 h-4" /></Button>
                    </a>
                    <a href={fileUrl(s.audio_url)} target="_blank" rel="noreferrer" title="Audio">
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-zinc-400 hover:text-cyan-400"><Download className="w-4 h-4" /></Button>
                    </a>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-zinc-400 hover:text-violet-400"
                      onClick={() => setEditing({ ...s })} data-testid={`edit-${s.id}`}><Edit3 className="w-4 h-4" /></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-zinc-400 hover:text-rose-400" data-testid={`delete-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-zinc-950 border-zinc-800">
                        <AlertDialogHeader><AlertDialogTitle>Delete "{s.song_title}"?</AlertDialogTitle>
                          <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-zinc-900 border-zinc-800">Cancel</AlertDialogCancel>
                          <AlertDialogAction className="bg-rose-500 hover:bg-rose-600" onClick={() => del(s.id)} data-testid={`confirm-delete-${s.id}`}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </td>
              </tr>
            ))}
            {subs.length === 0 && <tr><td colSpan="8" className="p-12 text-center text-zinc-500">No submissions found</td></tr>}
          </tbody>
        </table>
      </div>

      {/* View + change history */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-3xl bg-zinc-950 border-zinc-800 max-h-[85vh] overflow-y-auto" data-testid="view-sub-dialog">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl flex items-center gap-3">
                  {viewing.song_title}<StatusBadge status={viewing.status} />
                </DialogTitle>
              </DialogHeader>
              <div className="grid md:grid-cols-2 gap-6 mt-4">
                <div>
                  <img src={fileUrl(viewing.artwork_url)} className="w-full aspect-square rounded-lg object-cover border border-zinc-800" alt="art" />
                  <div className="mt-4"><AudioPlayer src={viewing.audio_url} testId="admin-view-player" /></div>
                </div>
                <div className="space-y-2 text-sm">
                  <R k="Submission ID" v={<code className="text-cyan-400 text-xs">{viewing.id}</code>} />
                  <R k="User" v={`${viewing.user?.full_name} · ${viewing.user?.email}`} />
                  <R k="Primary Artist" v={viewing.primary_artist_name} />
                  <R k="Instagram" v={viewing.primary_artist_instagram} />
                  <R k="Spotify" v={viewing.primary_artist_spotify} />
                  <R k="ISRC" v={<code className="text-emerald-400">{viewing.isrc || "—"}</code>} />
                  <R k="UPC" v={<code className="text-emerald-400">{viewing.upc || "—"}</code>} />
                  <R k="Language" v={viewing.language} />
                  <R k="Genre" v={viewing.genre} />
                  <R k="Release Date" v={viewing.release_date} />
                  <R k="Last Edited" v={viewing.last_edited_at ? new Date(viewing.last_edited_at).toLocaleString() : "—"} />
                </div>
              </div>

              {viewing.change_history?.length > 0 && (
                <div className="mt-6 p-4 rounded-xl border border-amber-500/30 bg-amber-500/5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-amber-300 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> Change History</h3>
                    {viewing.pending_review && (
                      <Button size="sm" onClick={() => markReviewed(viewing.id)} data-testid={`mark-reviewed-${viewing.id}`}
                        className="bg-emerald-500 hover:bg-emerald-600 text-zinc-950 rounded-full">
                        <CheckCheck className="w-3.5 h-3.5 mr-1" /> Mark Changes as Reviewed
                      </Button>
                    )}
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {viewing.change_history.slice().reverse().map((c, i) => (
                      <div key={i} className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 text-xs" data-testid={`change-${i}`}>
                        <div className="flex justify-between text-zinc-500 mb-1">
                          <span className="uppercase tracking-wider text-cyan-400 font-semibold">{c.field.replace(/_/g, " ")}</span>
                          <span>{new Date(c.changed_at).toLocaleString()} · {c.changed_by}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div><span className="text-rose-400">Old:</span> <span className="text-zinc-300 line-through">{String(c.old || "—")}</span></div>
                          <div><span className="text-emerald-400">New:</span> <span className="text-zinc-100">{String(c.new || "—")}</span></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg bg-zinc-950 border-zinc-800 max-h-[85vh] overflow-y-auto" data-testid="edit-sub-dialog">
          <DialogHeader><DialogTitle>Edit Submission</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <F label="Song Title"><Input value={editing.song_title||""} onChange={(e)=>setEditing({...editing, song_title:e.target.value})} data-testid="edit-song-title" className="bg-zinc-900 border-zinc-800" /></F>
              <F label="Primary Artist"><Input value={editing.primary_artist_name||""} onChange={(e)=>setEditing({...editing, primary_artist_name:e.target.value})} className="bg-zinc-900 border-zinc-800" /></F>
              <div className="grid grid-cols-2 gap-3">
                <F label="ISRC"><Input value={editing.isrc||""} onChange={(e)=>setEditing({...editing, isrc:e.target.value})} data-testid="edit-isrc" className="bg-zinc-900 border-zinc-800 font-mono" /></F>
                <F label="UPC"><Input value={editing.upc||""} onChange={(e)=>setEditing({...editing, upc:e.target.value})} data-testid="edit-upc" className="bg-zinc-900 border-zinc-800 font-mono" /></F>
              </div>
              <F label="Status">
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger data-testid="edit-status" className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Notes"><Textarea rows={3} value={editing.notes||""} onChange={(e)=>setEditing({...editing, notes:e.target.value})} className="bg-zinc-900 border-zinc-800" /></F>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-zinc-700 hover:bg-zinc-900">Cancel</Button>
            <Button onClick={save} data-testid="edit-save" className="bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function R({ k, v }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-zinc-500 uppercase tracking-wider text-xs">{k}</span>
      <span className="text-zinc-200 text-right break-all">{v || "—"}</span>
    </div>
  );
}
function F({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-zinc-400">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
